import { ChangeDetectorRef, Component, ElementRef, HostListener, Inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { MAPS_HOST_ADAPTER, MapsHostAdapter, MapsLocation } from '../../host-adapter';
import { MapsSessionService } from '../../services/maps-session.service';
import { SavedSearchesService } from '../../services/saved-searches.service';
import { ContactService } from '../../services/contact.service';

/**
 * App chrome shown above the active view: a home button on the left and a
 * dropdown on the right that exposes the admin dashboard (admin only) and
 * sign-out. Hidden entirely on the pre-session locations (access, no-access)
 * and whenever there is no valid session, so the access flow stays clean.
 * Navigation goes through the host adapter, so the same header works both on
 * the standalone app's routes and inside the routerless MJExplorer surface.
 */
@Component({
    selector: 'maps-header',
    standalone: false,
    templateUrl: './header.component.html',
    styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, OnDestroy {
    /** Whether a valid session exists — drives whether the bar renders at all. */
    visible = false;
    isAdmin = false;
    menuOpen = false;
    /** Saved-search count for the nav badge ("99+" capped in the getter). */
    savedSearchCount = 0;

    private pageSubscription: Subscription | null = null;
    private locationSubscription: Subscription | null = null;

    constructor(
        private readonly session: MapsSessionService,
        @Inject(MAPS_HOST_ADAPTER) protected readonly hostAdapter: MapsHostAdapter,
        private readonly host: ElementRef<HTMLElement>,
        private readonly savedSearches: SavedSearchesService,
        private readonly contact: ContactService,
        private readonly zone: NgZone,
        private readonly cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.pageSubscription = this.savedSearches.Page$.subscribe((page) => {
            this.zone.run(() => {
                if (page) this.savedSearchCount = page.totalCount;
                this.cdr.detectChanges();
            });
        });
        // Location$ replays the current location on subscribe, so this both
        // initializes the bar and re-evaluates it on every navigation — the bar
        // appears once the user redeems a key and disappears after sign-out,
        // without a full reload.
        this.locationSubscription = this.hostAdapter.Location$.subscribe((location) => {
            const wasVisible = this.visible;
            this.updateVisibility(location);
            void this.refreshAdmin();
            if (this.visible && !wasVisible) void this.refreshSavedSearches();
        });
    }

    ngOnDestroy(): void {
        this.pageSubscription?.unsubscribe();
        this.locationSubscription?.unsubscribe();
    }

    get savedSearchBadge(): string {
        return this.savedSearchCount > 99 ? '99+' : String(this.savedSearchCount);
    }

    openSavedSearches(): void {
        this.menuOpen = false;
        this.savedSearches.OpenDrawer();
        // Fresh rows every open — another user on the same token may have
        // searched since the last load.
        void this.refreshSavedSearches();
    }

    openMessageHistory(): void {
        this.menuOpen = false;
        this.contact.OpenHistoryDrawer(); // the drawer loads a fresh page on every open
    }

    /** Badge load — silent on failure (a count is never worth an error). */
    private async refreshSavedSearches(): Promise<void> {
        try {
            await this.savedSearches.Refresh();
        } catch {
            // keep the last known count
        }
    }

    /**
     * Visibility is driven by the host location synchronously so the bar
     * renders with the rest of the page instead of popping in after a network
     * round-trip. The in-session views are already session-gated by the host;
     * only the pre-session locations hide the bar.
     */
    private updateVisibility(location: MapsLocation): void {
        this.visible = location !== 'access' && location !== 'no-access';
    }

    /**
     * isAdmin only decides whether the Admin Dashboard menu item shows, so it
     * can resolve a beat later without holding up the bar's appearance.
     */
    private async refreshAdmin(): Promise<void> {
        if (!this.visible) {
            this.isAdmin = false;
            return;
        }
        try {
            const status = await this.session.Validate();
            this.isAdmin = status.valid && status.isAdmin;
        } catch {
            this.isAdmin = false;
        }
    }

    toggleMenu(): void {
        this.menuOpen = !this.menuOpen;
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this.host.nativeElement.contains(event.target as Node)) {
            this.menuOpen = false;
        }
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        this.menuOpen = false;
    }

    goHome(): void {
        this.menuOpen = false;
        this.hostAdapter.NavigateTo('search');
    }

    goToDashboard(): void {
        this.menuOpen = false;
        this.hostAdapter.NavigateTo('admin-dashboard');
    }

    /** Leave MAPS for the embedding host's UI (session kept). Rendered only
     *  when the host adapter offers it — never on standalone, never for
     *  app-scoped magic-link guests. */
    returnToHost(): void {
        this.menuOpen = false;
        this.hostAdapter.ReturnToHost();
    }

    async signOut(): Promise<void> {
        this.menuOpen = false;
        try {
            await this.hostAdapter.SignOut();
        } finally {
            this.visible = false;
        }
    }
}
